/**
 * The "pinned block" context sent to the builder assistant.
 *
 * Pinned by one of three gestures, told apart by `intent`:
 *
 *  - `edit`       — the ✨ on a canvas block's toolbar, or "Modifier" in the
 *                   catalogue. That definition is where the agent STARTS; since
 *                   the single-nid binding was lifted (it must be able to edit a
 *                   family of blocks in one go), it may write to others — the
 *                   system prompt asks it to announce that, and Drupal's
 *                   permissions are what actually bound the write.
 *  - `create`     — "Créer un bloc" in the catalogue. No source, and no screen
 *                   context either: only the site the new definition must be
 *                   attached to (cf. `buildCreateContext`).
 *  - `createFrom` — "Créer un bloc à partir de celui-ci". The named definition
 *                   is a MODEL to copy and adapt; it must come out untouched, so
 *                   nothing is bound to it.
 *
 * Whatever the intent, the context is pinned in the chat panel and re-sent with
 * EVERY message: the sidecar renders it into the SYSTEM PROMPT of the run (see
 * services/claude-agent/src/agent.js).
 *
 * Deliberately LEAN: the agent runs under a 10k input-tokens-per-minute cap. We
 * send the block's identity plus a map of the page's block types — never the
 * `rjBuild` (the agent reads it itself with `get_block_def`) and never the other
 * blocks' data.
 *
 * WHERE the created block lands is NOT sent: the frontend places it itself (see
 * `blockPlacement.js`), so the agent is never handed a position it cannot act on.
 */

// Name of the window event carrying the context from the canvas to the panel.
// Same pattern as `builder:registry:invalidate` (BuilderChatPanel).
export const BLOCK_TARGET_EVENT = 'builder:chat:target'

// Past this, `data` is truncated: it is a hint about the block's content for the
// agent, not the source of truth. Mirrored server-side by BLOCK_DATA_MAX_CHARS
// in services/claude-agent/src/agent.js — keep the two in step.
const MAX_DATA_CHARS = 1500

/**
 * Serialise `value` as compact JSON, truncated to `max` characters. Always
 * returns a string (never an object) so the payload size is bounded in a way
 * the sidecar can rely on.
 */
export const truncateJson = (value, max = MAX_DATA_CHARS) => {
  let json
  try { json = JSON.stringify(value ?? {}) }
  catch { return '{}' }
  if (json.length <= max) return json
  return `${json.slice(0, max)}… (tronqué, ${json.length} caractères au total)`
}

/** Where the block sits, shared by every intent — enough to situate it, no more. */
const pageContext = (page, host) => ({
  slug: page?.slug ?? '',
  title: page?.title ?? '',
  kind: host ? host.kind : 'page',
  // Types only: cheap in tokens, enough to reason about the screen.
  blocks: (page?.blocs ?? []).map((b, i) => ({ i, type: b.type })),
})

/**
 * `edit` — a block instance on the canvas is the target.
 *
 * @param {object}   args
 * @param {object}   args.bloc  — the canvas block `{id, type, data, …}`
 * @param {object}   args.def   — its registry entry `{type, name, defNid, …}`
 * @param {number}   args.index — its position in `page.blocs`
 * @param {object}   args.page  — the BuilderContext `page` state
 * @param {object?}  args.host  — `{kind, id}` on a dashboard, `null` on a page
 */
export const buildBlockContext = ({ bloc, def, index, page, host }) => ({
  intent: 'edit',
  blocId: bloc.id,
  index,
  type: bloc.type,
  name: def?.name ?? bloc.type,
  defNid: def?.defNid ?? null,
  // THIS instance's current values — enough for the agent to know what the block
  // looks like on screen instead of guessing.
  data: truncateJson(bloc?.data),
  page: pageContext(page, host),
})

/**
 * `edit` from the catalogue, where there is no instance — only a definition.
 * Same intent and the same binding; only the instance data is missing.
 */
export const buildDefEditContext = ({ def, page, host }) => ({
  intent: 'edit',
  index: null,
  type: def.type,
  name: def.name ?? def.type,
  defNid: def.defNid ?? null,
  page: pageContext(page, host),
})

/**
 * `create` — no source, and no screen either: the editor describes from scratch.
 *
 * Deliberately the LEANEST of the three contexts: it carries the SITE, and
 * nothing else. Not the page, not its title, not the blocks sitting around the
 * « + » — a definition is a CATALOGUE entry, not a piece of that screen, and
 * handing over the neighbouring blocks pushes the model to copy them instead of
 * building what was asked.
 *
 * The site is the one piece that IS structural: a def belongs to a site's
 * catalogue (a group relation), and one created without it would never reappear
 * in that site's « Ajouter un bloc » (cf. le cloisonnement par site,
 * BuilderBlockCatalog). It is bound to the creation server-side — never chosen
 * by the model.
 */
export const buildCreateContext = ({ page }) => ({
  intent: 'create',
  site: { id: page?.siteId ?? null, name: page?.siteName ?? '' },
})

/**
 * `createFrom` — the named definition is a MODEL. It must survive untouched, so
 * `defNid` travels as `sourceDefNid`: nothing server-side binds a write to it.
 */
export const buildCreateFromContext = ({ def, page, host }) => ({
  intent: 'createFrom',
  sourceType: def.type,
  sourceName: def.name ?? def.type,
  sourceDefNid: def.defNid ?? null,
  page: pageContext(page, host),
})
